import React, { useMemo, useState } from "react";
import { PageSection } from "@/components/AppLayout.jsx";
import { AdminGuard } from "@/components/admin/AdminGuard.jsx";
import { ImportCsvPanel } from "@/components/admin/ImportCsvPanel.jsx";
import { EntityCrudPanel, DemoFlag } from "@/components/admin/EntityCrudPanel.jsx";
import { AdminAccountsAccessPanel } from "@/components/admin/AdminAccountsAccessPanel.jsx";
import { AdminOfficialListPrepPanel } from "@/components/admin/AdminOfficialListPrepPanel.jsx";
import { useKarateUi } from "@/hooks/useKarateUi.js";
import { getDocumentVisibilityWarningLocalized } from "@/i18n/phase1UiHelpers.js";
import { useHybridDataStore } from "@/hooks/useHybridDataStore.js";
import { useBackendCrudActions } from "@/hooks/useBackendCrudActions.js";
import { updateDocument } from "@/data/dataStore.js";
import { notifyDataUpdate } from "@/components/admin/useDataStore.js";
import { DocumentVisibilityBadge, EntityStatusBadge } from "@/components/DocumentVisibilityBadge.jsx";
import { DataTable } from "@/components/DataTable.jsx";
import {
  KarateAccountsPanel,
  KarateRolesPanel,
  KarateAccessRequestsPanel,
  KarateDojosOrgSummary,
} from "@/components/admin/KarateAdminPanels.jsx";
import { OfficialModelImportDemo045 } from "@/components/admin/OfficialModelImportDemo045.jsx";
import { useAuth } from "@/context/AuthContext.jsx";
import { HybridDataStatusBadge, BackendZoneBadge } from "@/components/BackendZoneBadge.jsx";
import { SEED_STUDENT_DEMO_CHILD_ID } from "@/data/phase1InitialSeed046.js";
import { DemoBadge } from "@/components/DemoBadge.jsx";
import { CkaImportOnAdminImportPage } from "@/pages/membership/AdminOfficialMembersPage.jsx";

export { HanshiDashboardPage } from "./HanshiDashboard.jsx";

export function AdminImportPage() {
  const { ui } = useKarateUi();
  const page = ui.adminPages.import;
  return (
    <AdminGuard>
      <PageSection title={page.title} subtitle={page.subtitle} testId="page-admin-import">
        <BackendZoneBadge variant="demoLocalAwaiting" testId="admin-import-backend-badge" />
        <OfficialModelImportDemo045 />
        <CkaImportOnAdminImportPage />
        <ImportCsvPanel />
      </PageSection>
    </AdminGuard>
  );
}

export function AdminDojosPage() {
  const { store, backendStatus, refreshBackend } = useHybridDataStore();
  const { dojosCrud } = useBackendCrudActions({
    backendStatus,
    dataSource: store._dataSource,
    refreshBackend,
  });
  const { ui, localizeDemo } = useKarateUi();
  const page = ui.adminPages.dojos;
  const senseiOptions = useMemo(
    () =>
      store.instructeurs
        .filter((i) => i.statut !== "inactif")
        .map((i) => ({ value: i.id, label: `${localizeDemo(i.nom)} (${i.titre})` })),
    [store.instructeurs, localizeDemo],
  );
  const statusOptions = useMemo(
    () => [
      { value: "actif", label: ui.status.actif },
      { value: "a_confirmer", label: ui.status.a_confirmer },
      { value: "inactif", label: ui.status.inactif },
    ],
    [ui],
  );

  return (
    <AdminGuard>
      <PageSection title={page.title} subtitle={page.subtitle} testId="page-admin-dojos">
        <HybridDataStatusBadge dataSource={store._dataSource} backendStatus={backendStatus} testId="admin-dojos-data-badge" />
        <KarateDojosOrgSummary dojos={store.dojos} instructeurs={store.instructeurs} etudiants={store.etudiants} />
        <EntityCrudPanel
          collectionKey="dojos"
          rows={store.dojos}
          testId="admin-dojos-crud"
          backendCrud={dojosCrud}
          searchFields={["nom", "ville", "id"]}
          columns={[
            { key: "nom", label: ui.columns.name, render: (r) => (<>{localizeDemo(r.nom)} <DemoFlag row={r} /></>) },
            { key: "ville", label: ui.columns.city, render: (r) => localizeDemo(r.ville) },
            {
              key: "sensei_id",
              label: ui.columns.senseiId,
              render: (r) => {
                const inst = store.instructeurs.find((i) => i.id === r.sensei_id);
                return inst ? localizeDemo(inst.nom) : r.sensei_id ?? "—";
              },
            },
            { key: "statut", label: ui.columns.status, render: (r) => <EntityStatusBadge statut={r.statut} /> },
          ]}
          fields={[
            { name: "id", label: "ID (unique)", required: true },
            { name: "nom", label: ui.columns.name, required: true },
            { name: "ville", label: ui.columns.city, required: true },
            { name: "adresse", label: ui.columns.address, required: true },
            { name: "telephone", label: ui.columns.phone },
            { name: "courriel", label: ui.columns.email },
            { name: "sensei_id", label: "Sensei", options: senseiOptions },
            { name: "statut", label: ui.columns.status, required: true, options: statusOptions },
          ]}
          buildItem={(form, existingId) => ({
            id: existingId || form.id.trim(),
            nom: form.nom.trim(),
            ville: form.ville.trim(),
            adresse: form.adresse.trim(),
            telephone: form.telephone.trim(),
            courriel: form.courriel.trim(),
            sensei_id: form.sensei_id || null,
            statut: form.statut || "a_confirmer",
            demo: form.nom.includes("[DÉMO]") || form.nom.includes("[DEMO]"),
          })}
        />
      </PageSection>
    </AdminGuard>
  );
}

export function AdminInstructorsPage() {
  const { store, backendStatus, refreshBackend } = useHybridDataStore();
  const { instructeursCrud } = useBackendCrudActions({
    backendStatus,
    dataSource: store._dataSource,
    refreshBackend,
  });
  const { ui, localizeDemo } = useKarateUi();
  const page = ui.adminPages.instructeurs;
  const dojoOptions = useMemo(
    () => store.dojos.map((d) => ({ value: d.id, label: localizeDemo(d.nom) })),
    [store.dojos, localizeDemo],
  );
  const statusOptions = useMemo(
    () => [
      { value: "actif", label: ui.status.actif },
      { value: "a_confirmer", label: ui.status.a_confirmer },
      { value: "inactif", label: ui.status.inactif },
    ],
    [ui],
  );

  return (
    <AdminGuard>
      <PageSection title={page.title} subtitle={page.subtitle} testId="page-admin-instructeurs">
        <HybridDataStatusBadge dataSource={store._dataSource} backendStatus={backendStatus} testId="admin-instructeurs-data-badge" />
        <EntityCrudPanel
          collectionKey="instructeurs"
          rows={store.instructeurs}
          testId="admin-instructeurs-crud"
          backendCrud={instructeursCrud}
          searchFields={["nom", "titre", "courriel"]}
          columns={[
            {
              key: "nom",
              label: ui.columns.name,
              render: (r) => (<>{localizeDemo(r.nom)} <DemoFlag row={r} /></>),
            },
            { key: "titre", label: ui.columns.title, render: (r) => localizeDemo(r.titre) },
            {
              key: "dojo_id",
              label: ui.columns.dojo,
              render: (r) => {
                const dojo = store.dojos.find((d) => d.id === r.dojo_id);
                return dojo ? localizeDemo(dojo.nom) : "—";
              },
            },
            { key: "statut", label: ui.columns.status, render: (r) => <EntityStatusBadge statut={r.statut} /> },
          ]}
          fields={[
            { name: "id", label: "ID", required: true },
            { name: "nom", label: ui.columns.name, required: true },
            { name: "titre", label: ui.columns.title, required: true },
            { name: "dojo_id", label: ui.columns.dojo, options: dojoOptions },
            { name: "courriel", label: ui.columns.email },
            { name: "telephone", label: ui.columns.phone },
            {
              name: "statut",
              label: ui.columns.status,
              required: true,
              options: statusOptions,
            },
          ]}
          buildItem={(form, existingId) => ({
            id: existingId || form.id.trim(),
            assignment_id: store.instructeurs.find((i) => i.id === existingId)?.assignment_id,
            nom: form.nom.trim(),
            titre: form.titre.trim(),
            dojo_id: form.dojo_id || null,
            courriel: form.courriel.trim(),
            telephone: form.telephone.trim(),
            statut: form.statut || "a_confirmer",
            demo: form.nom.includes("[DÉMO]") || form.nom.includes("[DEMO]"),
          })}
        />
      </PageSection>
    </AdminGuard>
  );
}

export function AdminStudentsPage() {
  const { store, backendStatus, refreshBackend } = useHybridDataStore();
  const { studentsCrud } = useBackendCrudActions({
    backendStatus,
    dataSource: store._dataSource,
    refreshBackend,
  });
  const { ui, localizeDemo } = useKarateUi();
  const page = ui.adminPages.etudiants;
  const dojoOptions = useMemo(
    () => store.dojos.map((d) => ({ value: d.id, label: localizeDemo(d.nom) })),
    [store.dojos, localizeDemo],
  );
  const statusOptions = useMemo(
    () => [
      { value: "actif", label: ui.status.actif },
      { value: "a_confirmer", label: ui.status.a_confirmer },
      { value: "inactif", label: ui.status.inactif },
    ],
    [ui],
  );

  return (
    <AdminGuard>
      <PageSection title={page.title} subtitle={page.subtitle} testId="page-admin-etudiants">
        <HybridDataStatusBadge dataSource={store._dataSource} backendStatus={backendStatus} testId="admin-etudiants-data-badge" />
        <EntityCrudPanel
          collectionKey="etudiants"
          rows={store.etudiants}
          testId="admin-etudiants-crud"
          backendCrud={studentsCrud}
          searchFields={["prenom", "nom", "groupe"]}
          columns={[
            {
              key: "nom",
              label: ui.columns.name,
              render: (r) => localizeDemo(`${r.prenom} ${r.nom}`.trim()),
            },
            {
              key: "dojo_id",
              label: ui.columns.dojo,
              render: (r) => {
                const dojo = store.dojos.find((d) => d.id === r.dojo_id);
                return dojo ? localizeDemo(dojo.nom) : r.dojo_id;
              },
            },
            { key: "groupe", label: ui.columns.group, render: (r) => localizeDemo(r.groupe) },
            { key: "ceinture", label: ui.columns.belt, render: (r) => localizeDemo(r.ceinture) },
            { key: "statut", label: ui.columns.status, render: (r) => <EntityStatusBadge statut={r.statut} /> },
          ]}
          fields={[
            { name: "id", label: "ID", required: true },
            { name: "prenom", label: ui.columns.firstName, required: true },
            { name: "nom", label: ui.columns.name, required: true },
            { name: "dojo_id", label: ui.columns.dojo, required: true, options: dojoOptions },
            { name: "groupe", label: ui.columns.group, required: true },
            { name: "ceinture", label: ui.columns.belt, required: true },
            { name: "statut", label: ui.columns.status, required: true, options: statusOptions },
          ]}
          buildItem={(form, existingId) => ({
            id: existingId || form.id.trim(),
            prenom: form.prenom.trim(),
            nom: form.nom.trim(),
            dojo_id: form.dojo_id,
            groupe: form.groupe.trim(),
            ceinture: form.ceinture.trim(),
            statut: form.statut || "a_confirmer",
            demo: form.prenom.includes("[DÉMO]") || form.nom.includes("[DÉMO]") || form.prenom.includes("[DEMO]") || form.nom.includes("[DEMO]"),
          })}
        />
      </PageSection>
    </AdminGuard>
  );
}

export function AdminDocumentsPage() {
  const { store } = useHybridDataStore();
  const { ui, localizeDemo, visibilityLabel, statusLabel, locale } = useKarateUi();
  const docs = ui.documents;
  const page = ui.adminPages.documents;
  const [editingId, setEditingId] = useState(null);
  const [visibilite, setVisibilite] = useState("a_valider");
  const [statut, setStatut] = useState("a_confirmer");
  const [message, setMessage] = useState("");

  const startEdit = (doc) => {
    setEditingId(doc.id);
    setVisibilite(doc.visibilite);
    setStatut(doc.statut);
    setMessage("");
  };

  const saveDoc = () => {
    const doc = store.documents.find((d) => d.id === editingId);
    const warning = getDocumentVisibilityWarningLocalized(visibilite, doc?.titre ?? "", locale);
    updateDocument(editingId, { visibilite, statut: visibilite === "public" ? "actif" : statut });
    notifyDataUpdate();
    setMessage(warning ? docs.savedWithWarning(warning) : docs.saved);
    setEditingId(null);
  };

  const visibilityOptions = useMemo(
    () =>
      ["public", "membres", "admin", "a_valider"].map((v) => ({
        value: v,
        label: visibilityLabel(v),
      })),
    [visibilityLabel],
  );

  const statusOptions = useMemo(
    () =>
      ["actif", "a_confirmer", "inactif"].map((s) => ({
        value: s,
        label: statusLabel(s),
      })),
    [statusLabel],
  );

  const columns = [
    { key: "titre", label: ui.columns.title, render: (r) => localizeDemo(r.titre) },
    { key: "categorie", label: ui.columns.category, render: (r) => localizeDemo(r.categorie) },
    {
      key: "visibilite",
      label: docs.visibility,
      render: (r) => <DocumentVisibilityBadge visibilite={r.visibilite} />,
    },
    { key: "statut", label: docs.status, render: (r) => <EntityStatusBadge statut={r.statut} /> },
    {
      key: "_actions",
      label: ui.crud.actions,
      sortable: false,
      render: (r) => (
        <button type="button" className="btn-link" onClick={() => startEdit(r)} data-testid={`admin-doc-edit-${r.id}`}>
          {ui.common.classify}
        </button>
      ),
    },
  ];

  return (
    <AdminGuard>
      <PageSection title={page.title} subtitle={page.subtitle} testId="page-admin-documents">
        <div className="warning-box" data-testid="doc-admin-warning">
          {docs.warning}
        </div>

        {editingId ? (
          <div className="crud-form" data-testid="admin-doc-form">
            <h3>{docs.classifyTitle}</h3>
            <label>
              {docs.visibility}
              <select value={visibilite} onChange={(e) => setVisibilite(e.target.value)} data-testid="admin-doc-visibilite">
                {visibilityOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            {getDocumentVisibilityWarningLocalized(visibilite, store.documents.find((d) => d.id === editingId)?.titre ?? "", locale) ? (
              <p className="warning-box" data-testid="admin-doc-sensitive-warning">
                {getDocumentVisibilityWarningLocalized(visibilite, store.documents.find((d) => d.id === editingId)?.titre ?? "", locale)}
              </p>
            ) : null}
            <label>
              {docs.status}
              <select value={statut} onChange={(e) => setStatut(e.target.value)} data-testid="admin-doc-statut">
                {statusOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <div className="btn-row">
              <button type="button" className="btn-primary" onClick={saveDoc} data-testid="admin-doc-save">{ui.crud.save}</button>
              <button type="button" className="btn-secondary" onClick={() => setEditingId(null)}>{ui.crud.cancel}</button>
            </div>
          </div>
        ) : null}

        {message ? <p className="import-message">{message}</p> : null}

        <DataTable
          rows={store.documents}
          columns={columns}
          searchFields={["titre", "categorie"]}
          filters={[
            {
              field: "visibilite",
              label: docs.visibility,
              options: visibilityOptions,
            },
          ]}
          emptyMessage={docs.empty}
          testId="admin-documents-table"
        />
      </PageSection>
    </AdminGuard>
  );
}

export { HanshiDashboardPage as AdminDashboardPage } from "./HanshiDashboard.jsx";

export function AdminAccountsPage() {
  const { demoAccounts } = useAuth();
  const { ui } = useKarateUi();
  const page = ui.adminPages.comptes;
  return (
    <AdminGuard>
      <PageSection title={page.title} subtitle={page.subtitle} testId="page-admin-comptes">
        <AdminAccountsAccessPanel />
        <KarateAccountsPanel accounts={demoAccounts} />
      </PageSection>
    </AdminGuard>
  );
}

export function AdminOfficialListPrepPage() {
  const { ui } = useKarateUi();
  const page = ui.adminPages.preparationListe;
  return (
    <AdminGuard>
      <PageSection title={page.title} subtitle={page.subtitle} testId="page-admin-preparation-liste">
        <AdminOfficialListPrepPanel />
      </PageSection>
    </AdminGuard>
  );
}

export function AdminRolesPage() {
  const { ui } = useKarateUi();
  const page = ui.adminPages.roles;
  return (
    <AdminGuard>
      <PageSection title={page.title} subtitle={page.subtitle} testId="page-admin-roles">
        <KarateRolesPanel />
      </PageSection>
    </AdminGuard>
  );
}

export function AdminRequestsPage() {
  const { accountRequests, updateAccountRequestStatus } = useAuth();
  const { ui } = useKarateUi();
  const page = ui.adminPages.demandes;
  return (
    <AdminGuard>
      <PageSection title={page.title} subtitle={page.subtitle} testId="page-admin-demandes">
        <KarateAccessRequestsPanel requests={accountRequests} onUpdateStatus={updateAccountRequestStatus} />
      </PageSection>
    </AdminGuard>
  );
}

export function AdminOrganisationPage() {
  const { store, backendStatus } = useHybridDataStore();
  const { ui } = useKarateUi();
  const page = ui.adminPages.organisation;
  return (
    <AdminGuard>
      <PageSection title={page.title} subtitle={page.subtitle} testId="page-admin-organisation">
        <HybridDataStatusBadge dataSource={store._dataSource} backendStatus={backendStatus} testId="admin-organisation-data-badge" />
        <KarateDojosOrgSummary dojos={store.dojos} instructeurs={store.instructeurs} etudiants={store.etudiants} />
      </PageSection>
    </AdminGuard>
  );
}

export function AdminParentsPage() {
  const { store, backendStatus } = useHybridDataStore();
  const { ui, localizeDemo } = useKarateUi();
  const page = ui.adminPages.parents ?? { title: "Parents", subtitle: "" };
  const parents = store.parents?.length
    ? store.parents
    : store._dataSource === "local"
      ? [{ id: "parent-demo-local", nom: "Parent démo [DÉMO]", statut: "actif", demo: true, childStudentIds: [SEED_STUDENT_DEMO_CHILD_ID] }]
      : [];

  return (
    <AdminGuard>
      <PageSection title={page.title} subtitle={page.subtitle} testId="page-admin-parents">
        <HybridDataStatusBadge dataSource={store._dataSource} backendStatus={backendStatus} testId="admin-parents-data-badge" />
        {parents.length === 0 ? (
          <p data-testid="admin-parents-empty">{ui.backendStaging.parentsEmpty}</p>
        ) : (
          <DataTable
            rows={parents}
            testId="admin-parents-table"
            columns={[
              { key: "nom", label: ui.columns?.name ?? "Nom", render: (r) => (<>{localizeDemo(r.nom)} {r.demo ? <DemoBadge /> : null}</>) },
              { key: "statut", label: ui.columns?.status ?? "Statut", render: (r) => <EntityStatusBadge statut={r.statut} /> },
              { key: "childStudentIds", label: ui.backendStaging.linkedChildren, render: (r) => (r.childStudentIds?.length ? r.childStudentIds.join(", ") : "—") },
            ]}
          />
        )}
      </PageSection>
    </AdminGuard>
  );
}
